import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Quotes } from "@phosphor-icons/react";

const studies = [
  {
    tag: "Fintech · Series C",
    company: "Northwind Capital",
    headline: "From 12-hour deploy cycles to 9 minutes — without an incident.",
    summary:
      "We rebuilt Northwind's deployment plane from a monolithic Jenkins farm to a declarative, multi-region pipeline. The result: 80× faster releases and a clean SOC 2 Type II audit four months ahead of schedule.",
    metrics: [
      { value: "80×", label: "Faster deployments" },
      { value: "0", label: "Severity-1 incidents" },
      { value: "−42%", label: "Compute spend" },
    ],
  },
  {
    tag: "AI Infrastructure",
    company: "Meridian AI",
    headline: "Scaling a model-serving fleet from 200 to 18,000 GPUs.",
    summary:
      "Meridian's inference platform was burning $11M/year on idle accelerators. Our predictive scaling engine and per-tenant capacity isolation reclaimed 38% of fleet capacity and slashed cold-start latency by 96%.",
    metrics: [
      { value: "18k", label: "GPUs orchestrated" },
      { value: "96%", label: "Lower cold start" },
      { value: "$4.1M", label: "Annual savings" },
    ],
  },
  {
    tag: "E-commerce · Public",
    company: "Helix Retail",
    headline: "Black Friday at half the cost, twice the throughput.",
    summary:
      "Helix processes 11M orders during peak. We replaced their elastic tier with a predictive, edge-resident topology. Peak day handled 23M orders. Infra cost went down.",
    metrics: [
      { value: "2.1×", label: "Peak throughput" },
      { value: "−51%", label: "Peak-day cost" },
      { value: "99.999%", label: "Uptime during peak" },
    ],
  },
  {
    tag: "Healthcare · HIPAA",
    company: "Veridian Health",
    headline: "Zero-trust rollout across 9 hospital networks.",
    summary:
      "A 14-month rollout completed in 5. Every workload is now identity-bound, every PHI access cryptographically attested, and the security team finally sleeps at night.",
    metrics: [
      { value: "5 mo", label: "Rollout time" },
      { value: "100%", label: "Identity coverage" },
      { value: "0", label: "Compliance findings" },
    ],
  },
];

export default function CaseStudiesPage() {
  return (
    <div data-testid="case-studies-page">
      {/* HERO */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <div className="eyebrow mb-6">Case Studies · Selected work</div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl"
          >
            Outcomes,
            <br />
            not <span className="text-blue-700">slideware.</span>
          </motion.h1>
          <p className="mt-8 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            A selection of recent engagements. Every number on this page was
            measured in production, attested by the customer, and audited by
            us.
          </p>
        </div>
      </section>

      {/* STUDIES */}
      {studies.map((s, i) => (
        <section
          key={s.company}
          data-testid={`case-study-${s.company.toLowerCase().replace(/\s+/g, "-")}`}
          className={`border-b border-zinc-200 ${i % 2 === 1 ? "bg-zinc-50" : "bg-white"}`}
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-blue-700">
                / {String(i + 1).padStart(2, "0")} — {s.tag}
              </div>
              <div className="font-display text-3xl mt-4 tracking-tight text-zinc-500">
                {s.company}
              </div>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                {s.headline}
              </h2>
              <p className="mt-6 text-zinc-700 text-lg leading-relaxed max-w-2xl">
                {s.summary}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="border-t border-l border-zinc-200 grid grid-cols-1">
                {s.metrics.map((m) => (
                  <div
                    key={m.label}
                    className="border-r border-b border-zinc-200 p-6 lg:p-8 bg-white"
                  >
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Result
                    </div>
                    <div className="font-display text-4xl lg:text-5xl tracking-tight mt-2">
                      {m.value}
                    </div>
                    <div className="text-sm text-zinc-600 mt-1">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* QUOTE */}
      <section className="border-b border-zinc-200 bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <Quotes size={48} weight="fill" className="text-blue-500" />
          <blockquote className="mt-8 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight max-w-4xl">
            &ldquo;Infranova is the only vendor we&apos;ve ever onboarded where the
            people in the room on day one were still in the room on day 200.
            That continuity is the product.&rdquo;
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-blue-700 grid place-items-center font-display font-bold text-white">
              EM
            </div>
            <div>
              <div className="font-medium">Elena Marquez</div>
              <div className="text-sm text-zinc-400">
                VP Engineering, Meridian AI
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-24 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">
            Want to be the next case study?
          </h2>
          <Link
            to="/contact"
            data-testid="case-studies-cta"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-7 py-4 text-base font-medium hover:bg-blue-800 transition-colors w-fit"
          >
            Talk to engineering
            <ArrowUpRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
